<template>
  <div class="site-header-container">
    <header
      ref="siteHeader"
      class="site-header"
      :class="[$class(headerState)]"
      data-sign-post-priority="1"
      :js-sign-post="$string('accessibility.sign_posts.site_header')"
    >
      <div class="site-header__masthead">
        <div
          v-if="enableAnnouncements"
          ref="siteHeaderAnnouncement"
          class="site-header__container site-header__container__top"
        >
          <announcement-bar
            :announcements="announcements"
            :delay="liquid.settings.announcement_delay"
            :showCounter="liquid.settings.show_counter"
            :endDate="liquid.settings.counter_end_date"
            :endDateText ="liquid.settings.counter_text"
          />
        </div>

        <div class="site-header__container container site-header__container__menu">
          <!-- LEFT NAV -->
          <nav class="site-header__left-nav site-header__navigation">
            <ul class="site-header__navigation-list">
              <li
                v-for="(link, index) in leftNavLinksLinks"
                :key="'left-nav-link-' + index"
                class="site-header__navigation-item body-2"
              >
                <a
                  class="site-header__navigation-link"
                  :href="link.settings.url"
                >
                  <span
                    v-if="link.settings.icon"
                    v-html="$icon(link.settings.icon.trim())"
                  />
                  <span>{{ link.settings.title }}</span>
                </a>
              </li>
            </ul>
          </nav>
          <!-- END LEFT NAV -->

          <div class="site-header__logo">
            <a
              :aria-label="$string('accessibility.home')"
              :href="$variable('routes.root')"
              class="site-header__logo-link"
              :class="school && school.updated ? 'school-logo-link' : ''"
            >
              <img
                v-if="$variable('store.logo') && !school"
                :alt="$variable('store.shopName')"
                :src="$variable('store.logo')"
                width="91"
                height="26"
              >
              <img
                v-if="$variable('store.logo') && school"
                :alt="$variable('store.shopName')"
                :src="school && school.updated ? school.schoolLogoUrl : $variable('store.logo')"
                width="91"
                height="26"
              >
            </a>
            <!-- <span v-if="this.school" class="x-mark" style="color:black; font-size:8pt; margin-right: 4px;">&#x2715;</span>
            <img
              v-if="this.school"
              :src="this.school.schoolLogoUrl"
              class="school-logo-desktop"
              width="91"
              height="26"
            >
            <img
              v-if="this.school"
              :src="this.school.schoolLogoUrl"
              class="school-logo"
              width="91"
              height="26"
            > -->
          </div>

          <button
            aria-label="Search"
            class="icon-button search mobile"
            @click="toggleMobileSearchbar"
          >
            <span v-html="$icon('misc.search')" />
          </button>

          <div class="site-header__searchbar-container">
            <form
              autocomplete="off"
              class="site-header__searchbar desktop"
              @submit.prevent="search"
            >
              <input
                type="text"
                placeholder="Search"
                name="q"
                autocomplete="off"
                @input="onSearchChange"
              >
              <button
                type="submit"
                value="Search"
                aria-label="Search"
                class="icon-button"
              >
                <span v-html="$icon('misc.search')" />
              </button>
            </form>
          </div>

          <a
            :aria-label="$string('general.site_header.wishlist')"
            :href="$variable('routes.giftReggie.wishlist')"
            class="site-header__wishlist icon-button"
            @click.prevent="handleTracking"
          >
            <span v-html="$icon('misc.heart')" />
          </a>

          <a
            :aria-label="$string('general.site_header.account')"
            :href="$variable('routes.account.url')"
            class="site-header__account icon-button"
          >
            <span v-html="$icon('misc.account')" />
            <account-sub-menu
              v-if="$variable('customer.loggedIn') && !isMobile"
              :desktop-account="navigation.desktopAccountMenu"
              :logout-url="logoutUrl"
            />
          </a>

          <a
            :aria-label="$string('general.site_header.cart')"
            :href="$variable('routes.cart.url')"
            class="site-header__cart icon-button"
          >
            <span
              class="site-header__cart-icon"
              v-html="$icon('misc.cart')"
            />
            <span
              v-if="cart.item_count > 0"
              aria-hidden="true"
              class="site-header__cart-badge subtitle-2"
              v-text="cart.item_count"
            />
          </a>

          <button
            :aria-label="$string('general.site_header.menu')"
            class="site-header__menu icon-button"
            @click="toggleOverlay({
              component: 'menu-drawer',
              ignoreDismissed: true,
              namespace: 'menuDrawer',
            })"
            @mousedown="loadComponent('menu-drawer')"
            @touchstart="loadComponent('menu-drawer')"
          >
            <span v-html="$icon(menuOpen ? 'enclosed.close' : 'misc.menu')" />
          </button>
        </div>

        <div class="site-header__container container nav1">
          <nav class="site-header__navigation site-header__navigation--right">
            <ul class="site-header__navigation-list">
              <li
                v-for="(tier1, index) in navigation.desktopRight"
                :key="tier1.handle"
                class="site-header__navigation-item body-2"
                @mouseenter="
                  handleIconHover($event, tier1, navIcons[index]);
                  resetMegaNav();
                  closeOverlay('megaNav');
                "
                @mouseleave="handleIconHover($event, tier1, navIcons[index])"
              >
                <a
                  v-if="index === 0"
                  :target="getTargetForUrl(tier1.url)"
                  :href="tier1.url.includes('visualizer') ? tier1.url + '/headboard' : tier1.url"
                  class="site-header__navigation-link1"
                >
                  <span>{{ tier1.title }}</span>
                </a>
                <a
                  v-if="index > 0"
                  :target="getTargetForUrl(tier1.url)"
                  :href="tier1.url.includes('visualizer') ? tier1.url + '/headboard' : tier1.url"
                  class="site-header__navigation-link1"
                  @click.prevent="handleTracking"
                >
                  <span>{{ tier1.title }}</span>
                </a>
              </li>
            </ul>
          </nav>
        </div>

        <div
          ref="secondNav"
          class="site-header__container container nav2"
          @mouseleave="handleMegaNav"
        >
          <nav class="site-header__navigation">
            <ul class="site-header__navigation-list">
              <template
                v-for="(tier1, tier1Index) in navigation.desktop"
                :key="tier1.handle"
              >
                <li
                  v-if="liquid.settings.school_enable_desktop && liquid.settings.school_position_desktop === tier1Index"
                  class="site-header__navigation-item body-2"
                  @mouseenter="openSchoolsNav"
                  @mouseleave="openSchoolsNav"
                >
                  <a
                    :href="liquid.settings.school_header_link_desktop ?? '#'"
                    class="site-header__navigation-link"
                    :class="{
                      'site-header__navigation-link--active': schoolNavOpen,
                    }"
                  >{{ liquid.settings.school_label_desktop }}</a>
                </li>
                <li
                  class="site-header__navigation-item body-2"
                  @mouseenter="handleMegaNav($event, tier1, tier1Index)"
                  @keypress="handleMegaNav($event, tier1, tier1Index)"
                >
                  <button
                    v-if="tier1.links.length && !tier1.url"
                    class="site-header__navigation-link"
                    :class="{
                      'site-header__navigation-link--active': tier1Index === megaNav.index,
                      [`site-header__navigation-link--${tier1.options?.color}`]: tier1.options?.color
                    }"
                    v-text="tier1.title"
                  />
                  <a
                    v-else
                    :href="tier1.url"
                    :target="getTargetForUrl(tier1.url)"
                    class="site-header__navigation-link"
                    :class="{
                      'site-header__navigation-link--active': tier1Index === megaNav.index,
                      [`site-header__navigation-link--${tier1.options?.color}`]: tier1.options?.color
                    }"
                    v-text="tier1.title"
                  />
                </li>
              </template>
            </ul>
          </nav>
        </div>

        <div
          class="site-header__searchbar-mobile container"
          style="display: none;"
        >
          <form
            action="/search"
            class="site-header__searchbar"
          >
            <input
              type="text"
              placeholder="Search"
              name="q"
              autocomplete="off"
              @input="onSearchChange"
            >
            <button
              type="submit"
              value="Search"
              aria-label="Search"
              class="icon-button"
            >
              <span v-html="$icon('misc.search')" />
            </button>
          </form>
        </div>
      </div>
    </header>

    <overlay
      key="menuDrawer"
      block-class="menu-drawer-overlay"
      direction="right"
      namespace="menuDrawer"
      :template="false"
      type="drawer"
    >
      <template #body="{ isActive }">
        <menu-drawer
          v-if="getComponentVisible('menu-drawer')"
          v-cloak
          :is-active="isActive"
          :navigation="navigation.mobile"
          :tier-two-features="tierTwoFeatures"
          :drawer-top-features="drawerTopFeatures"
          :footer-links="drawerFooterLinks"
          :schools="liquid.settings.school_enable_mobile ? schools : null"
          :schools-label="liquid.settings.school_label_mobile"
          :schools-position="liquid.settings.school_position_mobile"
          :schools-name-col="liquid.settings.schools_name_col"
          :schools-letter-col="liquid.settings.schools_letter_col"
          :schools-logo-col="liquid.settings.schools_logo_col"
          :schools-link-col="liquid.settings.schools_link_col"
          :schools-not-found-label="liquid.settings.schools_not_found_label"
          :schools-not-found-link="liquid.settings.schools_not_found_link"
        >
          <template
            v-if="liquid.settings.enable_evergreen && liquid.settings.evergreen_text"
            #evergreenAnnouncement
          >
            <eyebrow
              :enable-bullet="liquid.settings.evergreen_bullet"
              :text="liquid.settings.evergreen_text"
              modifier="eyebrow-bullet--small"
              text-modifiers="caption"
            />
          </template>
        </menu-drawer>
      </template>
    </overlay>

    <overlay
      :template="false"
      block-class="shop-by-school-nav-overlay"
      direction="down"
      namespace="shopBySchoolNav"
      type="modal"
    >
      <template #body="{ isActive, resetScroll, scrollElement }">
        <shop-by-school-nav
          :schools="liquid.settings.school_enable_desktop ? schools : null"
          :is-active="isActive"
          :reset-scroll="resetScroll"
          :scroll-element="scrollElement || null"
          :schools-name-col="liquid.settings.schools_name_col"
          :schools-letter-col="liquid.settings.schools_letter_col"
          :schools-logo-col="liquid.settings.schools_logo_col"
          :schools-link-col="liquid.settings.schools_link_col"
          :school-letter-nav-width-desktop="liquid.settings.school_letter_nav_width_desktop"
          :schools-not-found-label="liquid.settings.schools_not_found_label"
          :schools-not-found-link="liquid.settings.schools_not_found_link"
        />
      </template>
    </overlay>

    <overlay
      :template="false"
      block-class="mega-nav-overlay"
      direction="down"
      namespace="megaNav"
      type="modal"
    >
      <template #body="{ isActive, resetScroll, scrollElement }">
        <mega-nav
          :banners="megaNav.banners"
          :index="megaNav.index"
          :is-active="isActive"
          :menu="megaNav.menu"
          :reset-scroll="resetScroll"
          :scroll-element="scrollElement || null"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 *
 */
import { defineAsyncComponent, nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { debounce, getIcon, handleIconHover, throttle } from '~/helpers/general'
import timings from '~/config/timings'
import { mobileFooterLinksType, topFeaturesType, leftNavDesktopLinksType } from './constants'
import Cookies from 'js-cookie'

import classes from '~/config/classes'

export default {
  name: 'SiteHeader',

  components: {
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        blocks: [],
        settings: [],
      }),
    },
    navigation: {
      type: Object,
      default: () => ({
        desktop: [],
        desktopRight: [],
        mobile: [],
        desktopAccountMenu: [],
      }),
    },
    titleElement: {
      type: String,
      default: 'h1',
    },
    logoutUrl: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      announcements: this.liquid.blocks.filter(block => block.type === 'announcement'),
      getIcon,
      handleIconHover,
      height: {
        total: 0,
        visible: 0,
        announcement: 0,
      },
      megaNav: {
        menu: null,
        banners: [],
      },
      navIcons: [],
      tierTwoFeatures: this.getTierTwoFeatures(),
      isSticky: true,
      previousScrollTop: 0,
      sectionWrapper: '',
      school: null,
      schools: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      headerState: (state) => state.index.scroll.header,
      loadedStylesheets: (state) => state.index.stylesheets,
      scrollY: (state) => state.index.scroll.y,
      isMobile: (state) => state.index.screen.mobile,
    }),

    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute array of page handles that hides announcement bar
     * @returns {String[]}
     */
    disabledAnnouncementPages() {
      return this.liquid.settings.announcement_exclude_pages
        .split(',')
        .map(handle => handle.trim())
        .filter(Boolean)
    },

    /**
     * Compute if announcements should be hidden.
     * @returns {Boolean}
     */
    enableAnnouncements() {
      let pageHandle = this.$variable('page.path') || '/'
      if (pageHandle !== '/') {
        pageHandle = pageHandle.split('/').pop()
      }

      return this.announcements.length &&
        this.liquid.settings.enable_announcements &&
        this.disabledAnnouncementPages.indexOf(pageHandle) === -1
    },

    /**
     * Compute if menu drawer is open
     * @returns {boolean}
     */
    menuOpen() {
      return this.getActiveOverlay === 'menuDrawer'
    },

    schoolNavOpen() {
      return this.getActiveOverlay === 'shopBySchoolNav'
    },

    /**
     * Returns the drawer top features configured in the section
     */
    drawerTopFeatures() {
      return this.liquid.blocks.filter(b => b.type === topFeaturesType)
    },

    /**
     * Returns the drawer footer links configured in the section
     */
    drawerFooterLinks() {
      return this.liquid.blocks.filter(b => b.type === mobileFooterLinksType)
    },

    /**
     * Returns the left nav links
     */
    leftNavLinksLinks() {
      return this.liquid.blocks.filter(b => b.type === leftNavDesktopLinksType)
    },
  },

  watch: {

    getActiveOverlay() {
      if (this.getActiveOverlay !== 'megaNav') {
        this.resetMegaNav()
      }

      if (
        this.getActiveOverlay === 'megaNav' ||
        this.getActiveOverlay === 'menuDrawer'
      ) {
        nextTick(() => {
          this.setHeights(true)
        })
      }
    },

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
        return
      }

      this.$refs.siteHeader.ontransitionend = (args) => {
        if (!args || args.propertyName !== 'transform') {
          return
        }

        this.setHeights()
      }
    },

    /**
     * Watch total height to update CSS variables.
     */
    'height.total': function () {
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.height.total}px`,
      )
    },

    /**
     * Watch visible height to update CSS variable.
     */
    'height.visible': function () {
      document.documentElement.style.setProperty(
        '--header-visible-height',
        `${this.height.visible}px`,
      )
    },

    /**
     * Watch visible height to update CSS variable.
     */
    'height.secondNav': function () {
      document.documentElement.style.setProperty(
        '--header-secondnav-height',
        `${this.height.secondNav}px`,
      )
    },

    /**
     * Watch announcement height to update CSS variable.
     */
    'height.announcement': function () {
      document.documentElement.style.setProperty(
        '--header-announcement-height',
        `${this.height.announcement}px`,
      )
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    loadedStylesheets() {
      if (!this.loadedStylesheets.includes('layout.theme.css')) {
        return
      }

      /**
       * Delay set height to wait for CSS to be parsed.
       */
      setTimeout(() => {
        this.setHeights()
      }, timings.s)
    },
  },

  beforeMount() {
    (async () => {

      const schoolCode = Cookies.get('sc')

      const getJSON = async url => {
        const response = await fetch(url)
        if (!response.ok)
          throw new Error(response.statusText)

        const data = response.json()
        return data
      }

      try {
        const schoolsJson = await getJSON("https://cdn.shopify.com/s/files/1/0607/1844/8811/files/schools.json?v=1729079867")

        if (schoolCode) {

          let school = schoolsJson.find(s => s.schoolCode.toLowerCase() === schoolCode?.toLowerCase())

          if (!school) {
            school = schoolsJson.find(s => s.schoolEduCode?.toLowerCase() === schoolCode?.toLowerCase())
          }

          this.school = school
        }



      } catch (ex) {
        console.error(ex)
      }
    })()
  },

  mounted() {
    try {
      this.schools = JSON.parse(this.liquid?.settings?.schools)
    } catch (ex) {}
    nextTick(() => {
      this.setAnnouncemnetHeight()
      this.setHeights(true)
    })

    this.setStickyWrapper()
    this.setEventListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadComponent: 'components/load',
      closeAll: 'overlays/closeAll',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Format tier 2 blocks into settings, block id, and an array of banners.
     * @returns {Object}
     */
    getTierTwoFeatures() {
      const features = {}

      this.liquid.blocks.forEach(block => {
        if (
          block.type !== 'tier_2_features' ||
          Number.isNaN(block.settings.parent_index)
        ) {
          return
        }

        /**
         * Format an array of banners to easily access in menu drawer and
         * mega nav.
         */
        const bannerSettings = []
        const bannerKeys = Object.keys(block.settings).filter(key => key.includes('banner'))

        bannerKeys.forEach(key => {
          const words = key.split('_')
          const newKey = words.slice(2).join('_')
          let index = words[1]

          if (Number.isNaN(index)) {
            return
          }

          index -= 1

          if (!bannerSettings[index]) {
            bannerSettings[index] = {}
          }

          if (block.settings[key]) {
            bannerSettings[index][newKey] = block.settings[key]
          }
        })

        features[block.settings.parent_index] = {
          ...block.settings,
          id: block.id,
          bannerSettings: bannerSettings.filter(settings => Object.values(settings).length),
        }
      })

      return features
    },


    /**
     * Opens school meganav
     */
    openSchoolsNav(event) {
      this.loadComponent('shop-by-school-nav')
      const { type, offsetY } = event

      //Close mega nav if mouseleave direction is upward
      if (
        type === 'mouseleave' && offsetY < 0
      ) {
        this.resetMegaNav()
        this.closeOverlay('shopBySchoolNav')
        document.body.classList.remove('clipped')
        return
      }
      if (this.schools) {
        document.body.classList.add('clipped')

        this.openOverlay({
          component: 'shop-by-school-nav',
          ignoreDismissed: true,
          namespace: 'shopBySchoolNav',
        })

        setTimeout(() => {
          this.setHeights()
        }, timings.s)
      }
    },

    /**
     * Open the selected mega nav
     * @param {Object} event - JS mouseenter, mouseleave, keypress events
     * @param {Object} menu - The menu object to display in the mega nav.
     * @param {Number} index - The tier 1 menu item 0 based index.
     */
    handleMegaNav(event, menu, index) {
      this.loadComponent('mega-nav')
      const { type, offsetY } = event

      /**
       * Close mega nav if mouseleave direction is upward or if the menu item
       * has no sub-links.
       */
      if (
        (type === 'mouseleave' && offsetY < 0) ||
        (type === 'mouseenter' && !menu.links.length)
      ) {
        this.resetMegaNav()
        this.closeOverlay('shopBySchoolNav')
        this.closeOverlay('megaNav')
        document.body.classList.remove('clipped')
        return
      }

      /**
       * Open mega nav on enter press only.
       */
      if (event.type === 'keypress' && event.key === 'Enter') {
        return
      }

      if (menu) {
        this.megaNav = {
          menu: menu,
          index,
          banners: this.tierTwoFeatures[index]?.bannerSettings || [],
        }

        document.body.classList.add('clipped')

        this.openOverlay({
          component: 'mega-nav',
          ignoreDismissed: true,
          namespace: 'megaNav',
        })

        /**
         * Check heights again after a delay to ensure browser completed any
         * scrolling.
         */
        setTimeout(() => {
          this.setHeights()
        }, timings.s)
      }
    },

    /**
     * Reset mega nav state.
     */
    resetMegaNav() {
      this.megaNav = {
        menu: null,
        index: -1,
        banners: [],
      }
    },

    /**
     * Set scroll event listeners
     * - Ommit on cart page
     */
    setEventListeners() {
      if (cnvs.page.type !== 'cart') {
        window.addEventListener('scroll', throttle(() => {
          this.handleStickyScrollHeader()
        }, timings.xs))
      }
    },

    /**
     * Selects the parent section for the sticky wrapper
     */
    setStickyWrapper() {
      this.sectionWrapper = this.$refs.siteHeader.closest('#shopify-section-site-header')
    },

    /**
     * Handles the scroll position detection and updates the sticky header
     */
    handleStickyScrollHeader() {
      if (this.getActiveOverlay) {
        return
      }

      const currentScrollTop = window.scrollY

      if (currentScrollTop <= this.height.announcement) {
        this.sectionWrapper.classList.remove(classes.sticky)
        this.sectionWrapper.classList.remove(classes.stickyHidden)
        this.isSticky = false
      } else if (currentScrollTop > this.height.announcement) {
        this.sectionWrapper.classList.add(classes.sticky)
        this.sectionWrapper.classList.remove(classes.stickyHidden)
        this.isSticky = true
      }

      this.previousScrollTop = window.scrollY
    },

    /**
     * Track events before navigating away.
     * @param {Object} event - Click event.
     */
    handleTracking(event) {
      const anchor = event.target.closest('a')
      if (anchor) {
        if (anchor.href.includes(this.$variable('routes.giftReggie.root'))) {
          this.$gtmTrack('NavClick', 'nav_giftregistry')
        } else if (anchor.href.includes(this.$variable('routes.giftReggie.wishlist'))) {
          this.$gtmTrack('NavClick', 'nav_wishlist')
        }
        window.open(anchor.href, this.getTargetForUrl(anchor.href))
      }
    },

    /**
     * Set heights of header.
     * - Height.announcement is used to offset the sticky header position
     * - Initializes only once
     */
    setAnnouncemnetHeight() {
      const { siteHeaderAnnouncement } = this.$refs
      if (!siteHeaderAnnouncement) {
        return
      }
      this.height.announcement = siteHeaderAnnouncement.offsetHeight
    },

    /**
     * Set heights of header.
     * - Height.total is used for body padding.
     * - Height.visible is used for overlays etc.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted) {
      if (mounted) {
        this.height.total = this.$refs.siteHeader.offsetHeight
      }

      const offsetHeight = !this.isSticky
        ? this.$refs.siteHeader.offsetHeight + this.$refs.siteHeader.getBoundingClientRect().top
        : this.height.total - this.height.announcement

      this.height.visible = (this.headerState === 'closed')
        ? 0
        : offsetHeight

      this.height.secondNav = (this.headerState === 'closed')
        ? 0
        : this.$refs.secondNav?.offsetHeight
    },

    toggleMobileSearchbar() {
      if (this.getActiveOverlay === 'menuDrawer') {
        this.closeOverlay('menuDrawer')
      }
      let mobileSearchbar = document.querySelector('.site-header__searchbar-mobile.container')
      mobileSearchbar.style.display = mobileSearchbar.style.display === 'none' ? 'block' : 'none'
    },

    search() {
      const searchValue = document.querySelector('.site-header__searchbar input').value
      window._learnq.push([
        'track',
        'Searched Site',
        {
          "SearchTerm": searchValue,
        },
      ])

      window.location.href = `/search?q=${searchValue}`
    },

    onSearchChange: debounce((evt) => {
      if (evt.target.value) {
        console.log('onSearchChange', evt.target.value)
        window._learnq.push([
          'track',
          'Searched Site',
          {
            "SearchTerm": evt.target.value,
          },
        ])
      }
    }, 500),
    getTargetForUrl(url) {
      try {
        const urlObj = new URL(url)
        if (urlObj.hostname.length > 0 && !urlObj.hostname.includes('.dormify.com')) {
          return '_blank'
        }
      } catch (e) {}
      return '_self'
    },
  },
}
</script>
